import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import { useTagAutocompleteV2Query } from '../../../../../queries/Tag/useTagAutocompleteV2Query';
import { Button } from '../../../../components-v2/Button';
import { Input } from '../../../../components-v2/Input';
import { Pill } from '../../../../components-v2/Pill';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components-v2/Popover';
import { styleSheet } from './styles';

interface IProps {
	trigger: React.ReactElement;
	onTagSelected(tag: Api.IAccountTag): void;
	onOpenChange?(open: boolean): void;
}

const debounceMs = 300;
const pageSize = 25;

export function TagPickerPopover({ trigger, onTagSelected, onOpenChange }: IProps) {
	const [open, setOpenState] = React.useState(false);
	const setOpen = (next: boolean) => {
		setOpenState(next);
		onOpenChange?.(next);
	};
	const [search, setSearch] = React.useState('');
	const debouncedSearch = useDebounceValue(search, debounceMs);

	const autocomplete = useTagAutocompleteV2Query({
		enabled: open && !!debouncedSearch,
		query: debouncedSearch,
		pageSize,
		enforcePageSize: true,
	});

	React.useEffect(() => {
		if (!open) {
			setSearch('');
		}
	}, [open]);

	const results: Api.IAccountTag[] = autocomplete.isSuccess
		? ((autocomplete.data.pages[0]?.values ?? []) as unknown as Api.IAccountTag[])
		: [];

	const handleSelect = (tag: Api.IAccountTag) => {
		onTagSelected(tag);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>{trigger}</PopoverTrigger>
			<PopoverContent align='start' styles={[styleSheet.popoverContent]}>
				<Input
					type='text'
					value={search}
					placeholder='Search tags...'
					autoFocus
					onChange={e => setSearch(e.currentTarget.value)}
					onClick={e => e.stopPropagation()}
				/>
				<div className={css(styleSheet.resultList)}>
					{!debouncedSearch && <div className={css(styleSheet.statusText)}>Start typing to search tags</div>}
					{!!debouncedSearch && autocomplete.isLoading && (
						<div className={css(styleSheet.statusText)}>Searching...</div>
					)}
					{!!debouncedSearch && autocomplete.isSuccess && results.length === 0 && (
						<div className={css(styleSheet.statusText)}>No tags match &quot;{debouncedSearch}&quot;</div>
					)}
					{results.map((tag, index) => (
						<Button
							key={`${tag.id ?? ''}-${tag.name ?? ''}-${index}`}
							variant='ghost'
							styles={[styleSheet.resultItem]}
							onClick={e => {
								e.stopPropagation();
								handleSelect(tag);
							}}
						>
							<Pill variant='tag' color={tag.color || 'blue'} label={tag.name || ''} />
						</Button>
					))}
				</div>
			</PopoverContent>
		</Popover>
	);
}
